import React from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { store } from './store/store'
import './i18n/i18n'
import App from './App.tsx'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import ListingDetail from './pages/ListingDetail'
import NewListing from './pages/NewListing'
import Tips from './pages/Tips'
import NotFound from './pages/NotFound'
import Login from './pages/Login'
import OAuthCallback from './pages/OAuthCallback'
import Register from './pages/Register'
import MyListings from './pages/MyListings'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ThemeModeProvider } from './context/ThemeModeContext'
import Layout from './components/Layout'
import { ToastProvider } from './components/ToastProvider'
import './index.css'

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  const { ready, authenticated } = useAuth()

  if (!ready) {
    return null
  }

  if (!authenticated) {
    const redirect = `${location.pathname}${location.search}`
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />
  }

  return <>{children}</>
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <ThemeModeProvider>
        <AuthProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <Layout>
              <Routes>
                <Route path="/" element={<App />} />
                <Route path="/listing/:id" element={<ListingDetail />} />
                <Route path="/tips" element={<Tips />} />
                <Route path="/login" element={<Login />} />
                <Route path="/oauth2/callback" element={<OAuthCallback />} />
                <Route path="/register" element={<Register />} />
                <Route
                  path="/my-listings"
                  element={(
                    <RequireAuth>
                      <MyListings />
                    </RequireAuth>
                  )}
                />
                <Route path="/account" element={<Navigate to="/my-listings" replace />} />
                <Route
                  path="/new"
                  element={(
                    <RequireAuth>
                      <NewListing />
                    </RequireAuth>
                  )}
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
            <ToastProvider />
          </BrowserRouter>
        </AuthProvider>
      </ThemeModeProvider>
    </Provider>
  </React.StrictMode>,
)
